import type { ColorButtonKey } from './types';

export type { ColorButtonKey };

export const lightThemeColors = {
  primary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
  },
  secondary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
  },
  neutral: {
    25: '#fefefe',
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    800: '#1f2937',
  },
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
  },
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
  },
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
  },
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
  },
};

export const gradientBgBase = 'bg-gradient-to-tr';
export const gradientBgPurplePink = `${gradientBgBase} from-purple-400 via-pink-500 to-red-500`;
export const gradientBgDark = `${gradientBgBase} from-slate-700 via-slate-900 to-slate-800`;
export const gradientBgPinkRed = `${gradientBgBase} from-pink-400 via-red-500 to-yellow-500`;

export const colorsBgLight = {
  white: 'bg-white text-gray-900',
  light: 'bg-white text-gray-900',
  contrast: 'bg-gray-800 text-white',
  success: 'bg-emerald-500 border-emerald-500 text-white',
  danger: 'bg-red-500 border-red-500 text-white',
  warning: 'bg-yellow-500 border-yellow-500 text-white',
  info: 'bg-blue-500 border-blue-500 text-white',
};

export const colorsText = {
  white: 'text-gray-900',
  light: 'text-gray-700',
  contrast: 'text-gray-800',
  success: 'text-emerald-500',
  danger: 'text-red-500',
  warning: 'text-yellow-500',
  info: 'text-blue-500',
};

export const colorsOutline = {
  white: [colorsText.white, 'border-gray-100'].join(' '),
  light: [colorsText.light, 'border-gray-100'].join(' '),
  contrast: [colorsText.contrast, 'border-gray-900'].join(' '),
  success: [colorsText.success, 'border-emerald-500'].join(' '),
  danger: [colorsText.danger, 'border-red-500'].join(' '),
  warning: [colorsText.warning, 'border-yellow-500'].join(' '),
  info: [colorsText.info, 'border-blue-500'].join(' '),
};

export const getButtonColor = (
  color: ColorButtonKey,
  isOutlined: boolean,
  hasHover: boolean,
  isActive = false,
): string => {
  if (color === 'void') {
    return '';
  }

  const colors = {
    ring: {
      primary: 'ring-sky-300',
      secondary: 'ring-slate-300',
      white: 'ring-gray-200',
      whiteDark: 'ring-gray-200',
      lightDark: 'ring-gray-200',
      contrast: 'ring-gray-300',
      success: 'ring-green-300',
      danger: 'ring-red-300',
      warning: 'ring-yellow-300',
      info: 'ring-blue-300',
      light: 'ring-gray-200',
    },
    active: {
      primary: 'bg-sky-700',
      secondary: 'bg-slate-200',
      white: 'bg-gray-100',
      whiteDark: 'bg-gray-100',
      lightDark: 'bg-gray-200',
      contrast: 'bg-gray-700',
      success: 'bg-green-700',
      danger: 'bg-red-700',
      warning: 'bg-yellow-700',
      info: 'bg-blue-700',
      light: 'bg-gray-100',
    },
    bg: {
      primary: 'bg-sky-500 text-white',
      secondary: 'bg-slate-100 text-slate-700',
      white: 'bg-white text-gray-900',
      whiteDark: 'bg-white text-gray-900',
      lightDark: 'bg-gray-100 text-gray-900',
      contrast: 'bg-gray-800 text-white',
      success: 'bg-green-500 text-white',
      danger: 'bg-red-500 text-white',
      warning: 'bg-yellow-500 text-white',
      info: 'bg-blue-500 text-white',
      light: 'bg-gray-50 text-gray-900',
    },
    bgHover: {
      primary: 'hover:bg-sky-600 hover:border-sky-600',
      secondary: 'hover:bg-slate-200 hover:border-slate-200',
      white: 'hover:bg-gray-50',
      whiteDark: 'hover:bg-gray-100',
      lightDark: 'hover:bg-gray-200',
      contrast: 'hover:bg-gray-700',
      success: 'hover:bg-green-600 hover:border-green-600',
      danger: 'hover:bg-red-600 hover:border-red-600',
      warning: 'hover:bg-yellow-600 hover:border-yellow-600',
      info: 'hover:bg-blue-600 hover:border-blue-600',
      light: 'hover:bg-gray-100',
    },
    borders: {
      primary: 'border-sky-500',
      secondary: 'border-slate-200',
      white: 'border-white',
      whiteDark: 'border-white',
      lightDark: 'border-gray-100',
      contrast: 'border-gray-800',
      success: 'border-green-500',
      danger: 'border-red-500',
      warning: 'border-yellow-500',
      info: 'border-blue-500',
      light: 'border-gray-200',
    },
    text: {
      primary: 'text-sky-600',
      secondary: 'text-slate-600',
      white: 'text-gray-900',
      whiteDark: 'text-gray-900',
      lightDark: 'text-gray-700',
      contrast: 'text-gray-800',
      success: 'text-green-600',
      danger: 'text-red-600',
      warning: 'text-yellow-600',
      info: 'text-blue-600',
      light: 'text-gray-600',
    },
    outlineHover: {
      primary: 'hover:bg-sky-500 hover:text-white hover:border-sky-500',
      secondary:
        'hover:bg-slate-100 hover:text-slate-700 hover:border-slate-300',
      white: 'hover:bg-gray-50 hover:text-gray-900',
      whiteDark: 'hover:bg-gray-100 hover:text-gray-900',
      lightDark: 'hover:bg-gray-200 hover:text-gray-900',
      contrast: 'hover:bg-gray-800 hover:text-gray-100',
      success: 'hover:bg-green-500 hover:text-white hover:border-green-500',
      danger: 'hover:bg-red-500 hover:text-white hover:border-red-500',
      warning: 'hover:bg-yellow-500 hover:text-white hover:border-yellow-500',
      info: 'hover:bg-blue-500 hover:text-white hover:border-blue-500',
      light: 'hover:bg-gray-100 hover:text-gray-900',
    },
  };

  const isOutlinedProcessed =
    isOutlined &&
    !['white', 'whiteDark', 'lightDark', 'light', 'secondary'].includes(color);

  const base = [colors.borders[color], colors.ring[color]];

  if (isActive) {
    base.push(colors.active[color]);
  } else {
    base.push(isOutlinedProcessed ? colors.text[color] : colors.bg[color]);
  }

  if (hasHover) {
    base.push(
      isOutlinedProcessed ? colors.outlineHover[color] : colors.bgHover[color],
    );
  }

  return base.join(' ');
};
